import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { getOrganizationById } from '../../api/firebase/organizations';
import useOrganizationImpersonation from '../../hooks/useOrganizationImpersonation';
import { paths } from '../../navigation/paths';
import { logError } from '../../rollbar';
import { LoadingIndicator } from '../Shared/BusyIndicator';

// Errors carry a kind, not a translated string, to keep t() out of the effect:
// useTranslation returns a fresh t each render, which would re-run it.
type Outcome =
  | { status: 'loading' }
  | { status: 'done' }
  | { status: 'notFound' }
  | { status: 'failed'; message: string };

/**
 * Deep-link impersonation target: /admin/impersonate/:organizationId
 *
 * Starts an impersonation session for one organization, gated by
 * `can_list_moxit_organizations` on the route -- e.g. from a one-click link in
 * the Zendesk support sidebar, instead of searching the providers list.
 *
 * Writes the same impersonatedOrganization/impersonatedDirectorId as the
 * ImpersonationButton, then redirects home. Unlike that button, which toggles
 * impersonation off when already impersonating, this always switches to the
 * organization in the URL.
 */
const ImpersonateRedirect: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { switchToOrganization } = useOrganizationImpersonation();
  const { t } = useTranslation();

  const [outcome, setOutcome] = useState<Outcome>({ status: 'loading' });

  useEffect(() => {
    // Cancel per effect, not once per mount: a mount-wide ref would also block
    // the re-run when :organizationId changes, since React keeps this instance.
    let cancelled = false;

    // Unreachable -- the route pattern requires the param. Narrows the type.
    if (!organizationId) return;

    const run = async () => {
      try {
        const organization = await getOrganizationById(organizationId);
        if (cancelled) return;

        if (!organization?.id) {
          setOutcome({ status: 'notFound' });
          return;
        }

        await switchToOrganization(organization);
        if (cancelled) return;

        setOutcome({ status: 'done' });
      } catch (error: unknown) {
        logError('ImpersonateRedirect: failed to start impersonation', error, { organizationId });
        if (cancelled) return;

        setOutcome({
          status: 'failed',
          message: error instanceof Error ? error.message : String(error),
        });
      }
    };

    setOutcome({ status: 'loading' });
    run();

    return () => {
      cancelled = true;
    };
  }, [organizationId, switchToOrganization]);

  if (outcome.status === 'done') {
    return <Navigate to={paths.home} replace />;
  }

  if (outcome.status === 'loading') {
    return <LoadingIndicator />;
  }

  return (
    <div className="p-8 text-center">
      <h3>{t('admin.impersonate.failed', 'Could not start impersonation')}</h3>
      <p>
        {outcome.status === 'notFound'
          ? t('admin.impersonate.notFound', 'Organization {{id}} was not found.', { id: organizationId })
          : outcome.message}
      </p>
    </div>
  );
};

export default ImpersonateRedirect;
